import { LegacyAudioRail } from '@shared/media/audio/legacy-audio-rail'
import type { LegacyAudioRailProps } from '@shared/media/audio/legacy-audio-rail/LegacyAudioRail'
import './learning-template-prompt-media.css'

export interface LearningTemplatePromptMediaProps {
  readonly audio?: string | null
  readonly audioAutoPlayKey?: string
  readonly audioAutoPlayOnLoad?: boolean
  readonly audioVariant?: LegacyAudioRailProps['variant']
  readonly className?: string
  readonly image?: string | null
  readonly imageAlt?: string
  readonly showAdvancedAudioControls?: boolean
}

/**
 * Shared prompt media contract used by self-learning and flashcard game
 * templates. When an image and audio are present, the audio rail overlays the
 * image just like the legacy self-learning exercise; audio-only prompts keep
 * the rail in normal flow.
 */
export function LearningTemplatePromptMedia({
  audio,
  audioAutoPlayKey,
  audioAutoPlayOnLoad = false,
  audioVariant = 'selfStudyBoxAudio',
  className = '',
  image,
  imageAlt = '',
  showAdvancedAudioControls,
}: LearningTemplatePromptMediaProps) {
  if (!image && !audio) {
    return null
  }

  const mediaClassName = ['learningTemplatePromptMedia', className]
    .filter(Boolean)
    .join(' ')

  if (!image) {
    return (
      <LegacyAudioRail
        autoPlayKey={audioAutoPlayKey}
        autoPlayOnLoad={audioAutoPlayOnLoad}
        className="learningTemplatePromptAudioRail"
        showAdvancedControls={showAdvancedAudioControls}
        source={audio ?? undefined}
        variant={audioVariant}
      />
    )
  }

  return (
    <div className={mediaClassName}>
      <img alt={imageAlt} src={image} />
      {audio ? (
        <LegacyAudioRail
          autoPlayKey={audioAutoPlayKey}
          autoPlayOnLoad={audioAutoPlayOnLoad}
          showAdvancedControls={showAdvancedAudioControls}
          source={audio}
          variant={audioVariant}
          withImage
        />
      ) : null}
    </div>
  )
}
